import { getFilteredDiscountTotal } from '@cart-services/core/cartService'
import { CountryCode } from '@constants'
import { type ChildOrder } from '@hooks-api/types/dataModel/fpOrder'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import type * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useTranslation } from 'react-i18next'
import { useContextOrderDetail } from '../OrderDetailProvider/OrderDetailProvider'

interface CollapseOrderInfoProps {
  order: ChildOrder
  rootStyle?: stylex.StyleXStyles
}

export function OrderAmount({ order, rootStyle }: CollapseOrderInfoProps) {
  const { subtotal, shipping_total: shippingTotal, tax, id, total, discount_items } = order
  const { t } = useTranslation()
  const { currency, transaction, countryCode } = useContextOrderDetail()
  const { child_orders, reward_point_discount, referral_code_discount, addon_v2_total, addon_v2_total_before_discount } = transaction || {}
  const shouldShowAddonPrice = (!!addon_v2_total && !!addon_v2_total_before_discount) && addon_v2_total > 0 && addon_v2_total_before_discount > 0

  // 目前後端訂單層級沒有好友點數，但預期未來會將扣點放在第一張訂單，所以先以第一張訂單的 id 來判斷是否為第一張單
  const isFirstOrder = id === child_orders?.[0]?.id
  const isUs = countryCode === CountryCode.US
  const filteredDiscountTotal = getFilteredDiscountTotal(discount_items || [])
  return (
    <VStack gap={2} rootStyle={rootStyle}>
      <Text variant="subtitle">{t('orderDetail.orderAmountSummary', { defaultValue: '訂單金額摘要' })}</Text>
      <HStack gap={1} justifyContent="space-between">
        <Text>{t('orderDetail.itemSubtotal', { defaultValue: '商品總計' })}</Text>
        <Text color={colors.grey800}>{formatPrice(currency, subtotal)}</Text>
      </HStack>
      <HStack gap={1} justifyContent="space-between">
        <Text>{t('orderDetail.discount', { defaultValue: '優惠折扣' })}</Text>
        <Text color={colors.grey800}>
          {`-${formatPrice(currency, Math.abs(filteredDiscountTotal ?? 0))}`}
        </Text>
      </HStack>
      {shouldShowAddonPrice && (
        <HStack gap={1} justifyContent="space-between">
          <Text>{t('orderDetail.addon', { defaultValue: '加價購' })}</Text>
          <HStack gap={1}>
            <Text data-testid="text-subtotal-before-discount" color={colors.grey500} lineThrough>
              {`$${addon_v2_total_before_discount.toLocaleString()}`}
            </Text>
            <Text data-testid="text-subtotal-after-discount">
              {`$${addon_v2_total.toLocaleString()}`}
            </Text>
          </HStack>
        </HStack>
      )}
      <HStack gap={1} justifyContent="space-between">
        <Text>{t('orderDetail.shippingFee', { defaultValue: '運費' })}</Text>
        <Text color={colors.grey800}>{formatPrice(currency, shippingTotal)}</Text>
      </HStack>
      <HStack gap={1} justifyContent="space-between">
        <Text>{t('orderDetail.pointsOrReferral', { defaultValue: '點數/好友推薦' })}</Text>
        <Text color={colors.grey800}>
          {`-${formatPrice(currency, isFirstOrder ? Math.abs(reward_point_discount ?? 0) + Math.abs(referral_code_discount ?? 0) : 0)}`}
        </Text>
      </HStack>
      {isUs && (
        <HStack gap={1} justifyContent="space-between">
          <Text>{t('orderDetail.tax', { defaultValue: '稅額' })}</Text>
          <Text color={colors.grey800}>
            {formatPrice(currency, tax ?? 0)}
          </Text>
        </HStack>
      )}
      <Divider />
      <HStack gap={1} justifyContent="space-between">
        <Text variant="subtitle">{t('orderDetail.orderTotal', { defaultValue: '訂單總額' })}</Text>
        <Text color={colors.grey800}>{formatPrice(currency, total)}</Text>
      </HStack>
    </VStack>
  )
}
